import React from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';


import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useConferenceAccountCheck} from 'src/hooks/useConferenceAccountCheck';
import {useTabRedirect} from 'src/hooks/useTabRedirect';
import {USERS_TYPES} from 'src/modules/profile/account/constants';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';

import {Group, Item} from '@pb/kit-component-group';
import {SecondaryTabSeparator, SecondaryTabs, Tab} from '@pb/kit-component-tabs-v2';
import {useGateValue} from '@pb/kit-lib-statsig';

import {
    usersGroupsHeader,
    usersGroupsHeaderTabs,
    usersGroupsHeaderTabsContainer,
} from './users-groups-header.module.styl';

type Props = {
    activeTab?: string,
    children: React.ReactNode,
};

export const UsersGroupsHeader = ({children, activeTab}: Props) => {
    const {accountId} = useParams<{accountId?: string}>();
    const redirectToSubTab = useTabRedirect(`/profile/account/${accountId}/users-groups/`);

    const {users} = useSelector(selectAccountDetails);
    const isConferenceAccount = useConferenceAccountCheck();
    const {hasAccess} = useAdminAccessRight();
    const provisioningEnabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);

    return (
        <>
            <div className={usersGroupsHeader}>
                <Item grow={1}>
                    {children}
                </Item>
            </div>
            {activeTab ? (
                <Group className={usersGroupsHeaderTabsContainer}>
                    <SecondaryTabs
                        className={usersGroupsHeaderTabs}
                        value={activeTab}
                        onChange={(tab: string) => redirectToSubTab(tab)}>
                        {[
                            <Tab key="all" caption={provisioningEnabled ? 'All Users' : 'All users'} value="all"/>,
                            ...Object.values(USERS_TYPES)
                                .filter(({key}) => !provisioningEnabled && users?.[key])
                                .map(({tab, caption, key}) => (
                                    <Tab key={key} caption={caption} value={tab}/>
                                )),
                            ...(users?.virtual || isConferenceAccount) ? [
                                <SecondaryTabSeparator key="separator-1"/>,
                                <Tab key="virtual" caption="Conference users" value="virtual"/>,
                            ] : [],
                            ...[
                                <SecondaryTabSeparator key="separator-2"/>,
                                <Tab key="upload-list" caption="Upload List" value="upload-list"/>,
                            ],
                            ...hasAccess(ACCESS_RIGHTS.GROUP_SCREEN_VIEW) ? [
                                <SecondaryTabSeparator key="separator-3"/>,
                                <Tab key="groups" caption="Groups" value="groups"/>,
                            ] : [],
                        ]}
                    </SecondaryTabs>
                </Group>
            ) : null}
        </>
    );
};
